import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  assessTableConfigId, assessTimeslot,
} from '../../../../../ui-kitchen/src/utils';
import { getLocationPath, getSearchParamsAsObject } from '../../../../../ui-kitchen/src/utils/locationUtils';
import { assessNotifyParams, getSelectResy } from './venueDetailsUtils';
import { DATE_TIME_FORMAT, DATE_FORMAT } from '../../../../../ui-kitchen/src/constants/dateFormats';
import PathBuilderContext from '../WidgetApp/PathBuilderContext';
import { setSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
// useVenueReservationsState Custom Hook
//
// Listens for venueReservationsStatus change, and updates venueReservationsState accordingly.
// Returns venueReservationsState

const PLATINUM_EXCLUSIVE_ID = 5035;

const useVenueReservationsState = ({
  Analytics,
  bookingState,
  dispatchVenueState,
  openNotify,
  platinumNight,
  searchParams,
  searchSessionId,
  selectedDate,
  selectedQuantity,
  setRouteBackPath,
  setCanBook,
  setDay,
  setNotifyData,
  setPartySize,
  setReservation,
  setReservationsByServicePeriod,
  setResyToken,
  setVenueId,
  setVenueName,
  venueReservationsStatus,
  venueReservationsData: venueReservationsDataRedux,
  venueState,
  filterWithDinnerService,
  isAuthenticated,
}) => {
  let venueReservationsData = venueReservationsDataRedux;
  if (filterWithDinnerService) {
    venueReservationsData = {
      ...venueReservationsDataRedux,
      reservations: venueReservationsDataRedux.reservations.filter((reservationItem) => reservationItem.serviceTypeId === 2),
    };
  }
  const [venueReservationsState, setVenueReservationsState] = useState({});
  const pathBuilder = useContext(PathBuilderContext);
  const navigate = useNavigate();
  useEffect(() => {
    /* istanbul ignore else */
    if (venueReservationsStatus === 'success' || venueReservationsStatus === 'error') {
      const nextState = {};
      if ('doubleBookingError' in venueReservationsData) {
        nextState.doubleBookingError = venueReservationsData.doubleBookingError;
        setVenueReservationsState(nextState);
      } else {
        const selectResy = getSelectResy({
          bookingState,
          featureRecaptcha: venueState.featureRecaptcha,
          openNotify,
          navigate,
          PathBuilder: pathBuilder,
          reservations: venueReservationsData.reservations,
          searchParams,
          setRouteBackPath,
          setReservation,
          setDay,
          setPartySize,
          setCanBook,
          setVenueId,
          setVenueName,
          selectedDate,
          selectedQuantity,
          setResyToken,
          venueId: venueState?.venueId,
          venueName: venueState?.venueName,
        });
        const handleReservationSelect = (reservation, notifyService) => {
          const config = {
            date: moment(selectedDate).format(DATE_FORMAT),
            seats: selectedQuantity,
          };

          const reservationPayload = { ...reservation };
          /* istanbul ignore else */
          if (reservation && typeof reservation === 'object') {
            const analyticsPayload = {
              venue: venueState.venueName,
              city: venueState?.venue?.location?.city,
              'is event': !!(reservation.event),
              event: reservation.event?.title,
              'party size': selectedQuantity,
              ...(reservation.time && { time: moment(reservation.time).format(DATE_TIME_FORMAT) }),
              'lead time': moment(reservation.time).diff(moment().startOf('day'), 'days'),
              searchSessionId,
            };
            Analytics.event('click booking slot', analyticsPayload);
            config.tableConfigId = reservation.token;
            reservationPayload.venueName = venueState.venueName;

            if (platinumNight && reservation?.exclusiveId === PLATINUM_EXCLUSIVE_ID) {
              reservationPayload.module = 'platinum night';
            }
          }
          /* istanbul ignore else */
          if (notifyService) {
            config.notifyService = notifyService;
            selectResy(config, null, true);
          } else if (reservation?.isEligible) {
            selectResy(config, reservationPayload, true);
          } else if (isAuthenticated) {
            setReservation({ ...reservation, venueName: venueState.venueName });
            nextState.displayGatingErrorModal = true;
            setVenueReservationsState({ ...nextState });
          } else {
            const nextPath = getLocationPath();
            navigate('/login');
            setSearchParams('next', nextPath);
            setSearchParams('token', reservation.token);
            setSearchParams('serviceTypeId', reservation.serviceTypeId);
            setSearchParams('exclusiveId', reservation.exclusiveId);
          }
        };

        const assessQueryParams = () => {
          const authSuccessRedirectUrl = searchParams.get('authSuccessRedirectUrl');
          const serviceId = searchParams.get('serviceId');
          const reservation = searchParams.get('reservation');
          const reservationFromSearch = reservation && JSON.parse(reservation);
          if (reservationFromSearch) {
            handleReservationSelect(reservationFromSearch);
          }

          if (authSuccessRedirectUrl && serviceId) {
            openNotify(serviceId);
          }
        };

        const closeModal = () => {
          setReservation({});
          nextState.displayGatingErrorModal = false;
          setVenueReservationsState(nextState);
        };

        nextState.reservations = venueReservationsData.reservations;
        nextState.notifyForUnavailable = venueReservationsData.notifyForUnavailable;
        nextState.activeNotifications = venueReservationsData.activeNotifications;
        nextState.doubleBookingError = venueReservationsData.error;
        nextState.filterQuery = venueReservationsData.query;
        // inline version of legacy controller assessReservations()
        nextState.hasAvailability = venueReservationsData.reservations.some((resy) => resy.available.length > 0);
        nextState.handleReservationSelect = handleReservationSelect;
        nextState.closeModal = closeModal;
        setVenueReservationsState(nextState);
        setReservationsByServicePeriod(venueReservationsData.reservations);

        assessNotifyParams(openNotify);
        assessTableConfigId({
          queryParams: getSearchParamsAsObject(),
          reservationsByServicePeriod: venueReservationsData.reservations,
          selectResy,
          setNotifyData,
        });
        assessTimeslot({
          queryParams: getSearchParamsAsObject(),
          reservationsByServicePeriod: venueReservationsData.reservations,
          selectResy,
          setNotifyData,
        });
        assessQueryParams();

        /* istanbul ignore else */
        if ('partnershipBranding' in venueReservationsData) {
          dispatchVenueState({ type: 'setParntershipBranding', payload: venueReservationsData.partnershipBranding });
        }
      }
    }
  }, [venueReservationsStatus, isAuthenticated]);

  return venueReservationsState;
};

export default useVenueReservationsState;
